import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { normalizarNombre } from "./AtencionPrioritariaContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PRIV_ROLES = ["admin", "administrador", "superadmin", "supervisor"];

const FacturaMismoMesContext = createContext(null);

export function FacturaMismoMesProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const [mapa, setMapa] = useState({});
  const [alerta, setAlerta] = useState(null);
  const dismissedRef = useRef(new Set());

  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const puedeMarcar = !!user && PRIV_ROLES.includes((user.role || "").toLowerCase());

  useEffect(() => {
    dismissedRef.current = new Set();
    setAlerta(null);
  }, [location.pathname]);

  const cargar = useCallback(async () => {
    if (!user || !token) return;
    try {
      const r = await axios.get(`${API}/factura-mismo-mes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const m = {};
      (r.data || []).forEach((d) => { m[d.nombre_norm] = d; });
      setMapa(m);
    } catch (e) { /* no crítico */ }
  }, [user, token]);

  useEffect(() => { cargar(); }, [cargar]);

  const getRecord = useCallback((nombre) => {
    const norm = normalizarNombre(nombre);
    if (!norm) return null;
    return mapa[norm] || null;
  }, [mapa]);

  const esMarcado = useCallback((nombre) => !!getRecord(nombre), [getRecord]);

  const notificarCliente = useCallback((nombre) => {
    const rec = getRecord(nombre);
    if (!rec) return;
    const norm = rec.nombre_norm;
    if (dismissedRef.current.has(norm)) return;
    setAlerta((prev) => (prev && prev.nombre_norm === norm ? prev : rec));
  }, [getRecord]);

  const cerrarAlerta = useCallback(() => {
    setAlerta((prev) => {
      if (prev) dismissedRef.current.add(prev.nombre_norm);
      return null;
    });
  }, []);

  const marcar = useCallback(async (nombre, motivo = "") => {
    const r = await axios.post(`${API}/factura-mismo-mes`, { nombre, motivo }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await cargar();
    return r.data;
  }, [token, cargar]);

  const desmarcar = useCallback(async (nombre) => {
    const r = await axios.delete(`${API}/factura-mismo-mes`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { nombre },
    });
    dismissedRef.current.delete(normalizarNombre(nombre));
    await cargar();
    return r.data;
  }, [token, cargar]);

  return (
    <FacturaMismoMesContext.Provider
      value={{ mapa, alerta, getRecord, esMarcado, notificarCliente, cerrarAlerta, marcar, desmarcar, recargar: cargar, puedeMarcar }}
    >
      {children}
    </FacturaMismoMesContext.Provider>
  );
}

export function useFacturaMismoMes() {
  const ctx = useContext(FacturaMismoMesContext);
  if (!ctx) {
    return {
      mapa: {}, alerta: null, getRecord: () => null, esMarcado: () => false,
      notificarCliente: () => {}, cerrarAlerta: () => {}, marcar: async () => {},
      desmarcar: async () => {}, recargar: async () => {}, puedeMarcar: false,
    };
  }
  return ctx;
}

// Watcher: las páginas lo renderizan donde tienen un nombre de cliente activo.
export function ClienteFacturaMesWatcher({ nombre }) {
  const { notificarCliente } = useFacturaMismoMes();
  useEffect(() => {
    if (nombre) notificarCliente(nombre);
  }, [nombre, notificarCliente]);
  return null;
}
